import { captureException } from '@/utils/sentry';
import { DataProxy, gql, useMutation } from '@apollo/client';
import { GET_VARIANT_PACK_LAST_PURCHASE_INFO_QUERY } from '@/contexts/variant-pack-info-context-queries';
import { getOperationName } from 'apollo-utilities';
import { isDefaultCart } from '@/utils/new-order';
import {
  NewOrder_ClearCartMutation,
  NewOrder_ClearCartMutationVariables,
  NewOrder_CreateOrderMutation,
  NewOrder_CreateOrderMutationVariables,
  NewOrder_DeleteOrderMutation,
  NewOrder_DeleteOrderMutationVariables,
  NewOrder_OrderContextQuery,
  NewOrder_OrderContextQueryVariables,
  NewOrder_OrderFieldsFragment,
  NewOrder_SubmitOrderMutation,
  NewOrder_SubmitOrderMutationVariables,
  NewOrder_UpdateCartMutation,
  NewOrder_UpdateCartMutationVariables,
  NewOrder_UpdateOrderAdditionalInputsMutation,
  NewOrder_UpdateOrderAdditionalInputsMutationVariables,
  NewOrder_UpdateOrderMetadataMutation,
  NewOrder_UpdateOrderMetadataMutationVariables,
  NewOrder_UpdateOrderNotesMutation,
  NewOrder_UpdateOrderNotesMutationVariables,
  NewOrder_UpdateOrderPoNumberMutation,
  NewOrder_UpdateOrderPoNumberMutationVariables,
  OrderDomain,
  OrderStatus,
} from '@/types';
import {
  ORDER_CONTEXT_QUERY,
  ORDER_FRAGMENT,
  useOrderContextQueryVariables,
} from '@/contexts/new-order-context-queries';
import {
  ORDER_HISTORY_QUERY,
  SEARCH_ORDER_HISTORY_QUERY,
} from './use-order-history-list-query';
import { OrderValidationErrorCode } from '@/enums';
import { PAYMENTS_CONTEXT_UNPAID_LIST_QUERY } from '@/contexts/payments-context';
import { PerfTiming, PerfTimingEvent } from '@/utils/perf-timing';
import { UNPLACED_ORDER_STATUSES } from '@domain/fulfillment-settings/blocs/order-availability';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useCallback } from 'react';
import { useOfflineModeEnabled } from './use-offline-mode-enabled';
import { useOfflineOrderContext } from './use-offline-data';
import { useOrderGuideStoreApi } from '@/stores/order-guide-store';
import { useUpdateFulfillment } from './use-update-fulfillment';

export const CREATE_ORDER_MUTATION = gql`
  mutation NewOrder_CreateOrder(
    $createdSource: OrderSource
    $orderDomain: OrderDomain
    $deliveryDate: String
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $fulfillmentType: String
    $timeslotEnd: String
    $timeslotStart: String
    $name: String
    $visibility: String
  ) {
    createOrder(
      created_source: $createdSource
      order_domain: $orderDomain
      delivery_date: $deliveryDate
      fulfillment_type: $fulfillmentType
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      fulfillment_timeslot_end: $timeslotEnd
      fulfillment_timeslot_start: $timeslotStart
      name: $name
      visibility: $visibility
    ) {
      order {
        ...NewOrder_OrderFields
      }
    }
  }

  ${ORDER_FRAGMENT}
`;

export const UPDATE_ORDER_METADATA_MUTATION = gql`
  mutation NewOrder_UpdateOrderMetadata(
    $orderUUID: uuid!
    $name: String
    $visibility: String
  ) {
    updateOrderMetadata(
      order_id: $orderUUID
      name: $name
      visibility: $visibility
    ) {
      storefront_order_metadata {
        name
        uuid
        visibility
      }
    }
  }
`;

export const UPDATE_ORDER_NOTES_MUTATION = gql`
  mutation NewOrder_UpdateOrderNotes($orderUUID: uuid!, $orderNotes: String) {
    updateOrderInputs(order_id: $orderUUID, order_notes: $orderNotes) {
      order {
        notes
        uuid
      }
    }
  }
`;

export const UPDATE_ORDER_PO_NUMBER_MUTATION = gql`
  mutation NewOrder_UpdateOrderPoNumber($orderUUID: uuid!, $poNumber: String) {
    updateOrderInputs(order_id: $orderUUID, po_number: $poNumber) {
      order {
        po_number
        uuid
      }
    }
  }
`;

export const UPDATE_ORDER_ADDITIONAL_INPUTS_MUTATION = gql`
  mutation NewOrder_UpdateOrderAdditionalInputs(
    $orderUUID: uuid!
    $additionalInputValues: [AdditionalInputValueInput!]
  ) {
    updateOrderInputs(
      additional_input_values: $additionalInputValues
      order_id: $orderUUID
    ) {
      order {
        additional_input_values
        uuid
      }
    }
  }
`;

export const DELETE_ORDER_MUTATION = gql`
  mutation NewOrder_DeleteOrder($orderUUID: uuid!) {
    deleteOrder(order_id: $orderUUID) {
      success
    }
  }
`;

export const SUBMIT_ORDER_MUTATION = gql`
  fragment NewOrder_PunchoutOrderMessageFields on PunchoutOrderMessage {
    order_message_url
    order_message_xml
  }

  mutation NewOrder_SubmitOrder(
    $orderUUID: uuid!
    $orderNotes: String
    $paymentMethod: String
    $poNumber: String
    $punchoutCookie: String
    $additionalInputValues: [AdditionalInputValueInput!]!
    $smartOrderUUID: uuid
  ) {
    submitOrder(
      additional_input_values: $additionalInputValues
      order_id: $orderUUID
      order_notes: $orderNotes
      payment_method: $paymentMethod
      po_number: $poNumber
      punchout_cookie: $punchoutCookie
      smart_order_id: $smartOrderUUID
    ) {
      loyalty_earns {
        order_id
        programs {
          earned_points
          program_id
          program_name
        }
      }
      order {
        ...NewOrder_OrderFields
      }
      punchout_order_message {
        ...NewOrder_PunchoutOrderMessageFields
      }
      split_order_id_list
    }
  }

  ${ORDER_FRAGMENT}
`;

export const UPDATE_CART_MUTATION = gql`
  mutation NewOrder_UpdateCart(
    $orderUUID: uuid!
    $updatedItems: [UpdateItemInput!]!
  ) {
    updateCart(order_id: $orderUUID, updated_items: $updatedItems) {
      order {
        ...NewOrder_OrderFields
      }
    }
  }

  ${ORDER_FRAGMENT}
`;

export const CLEAR_CART_MUTATION = gql`
  mutation NewOrder_ClearCart($orderUUID: uuid!) {
    clearCart(order_id: $orderUUID) {
      success
    }
  }
`;

type UpdateCacheFn = (
  cachedOrders: NewOrder_OrderContextQuery['getOrders'],
) => NewOrder_OrderContextQuery['getOrders'];

export const upsertCachedOrder =
  (order: NewOrder_OrderFieldsFragment): UpdateCacheFn =>
  (cachedOrders) => {
    if (!UNPLACED_ORDER_STATUSES.includes(order.status as OrderStatus)) {
      return cachedOrders.filter((entry) => entry.order?.uuid !== order.uuid);
    }
    return cachedOrders.some((entry) => entry.order?.uuid === order.uuid)
      ? cachedOrders.map((entry) =>
          entry.order?.uuid === order.uuid ? { ...entry, order } : entry,
        )
      : [{ __typename: 'OrderResponse' as const, order }, ...cachedOrders];
  };

export const removeCachedOrder =
  (orderUUID: string): UpdateCacheFn =>
  (cachedOrders) =>
    cachedOrders.filter((entry) => entry.order?.uuid !== orderUUID);

export const clearCachedOrderItems =
  (orderUUID: string): UpdateCacheFn =>
  (cachedOrders) => {
    const order = cachedOrders.find(
      (entry) => entry.order?.uuid === orderUUID,
    )?.order;
    if (!order) {
      return cachedOrders;
    }
    return isDefaultCart(order)
      ? cachedOrders.filter((entry) => entry.order?.uuid !== orderUUID)
      : cachedOrders.map((entry) =>
          entry.order?.uuid === orderUUID
            ? { ...entry, order: { ...entry.order, orders_items: [] } }
            : entry,
        );
  };

export const useNewOrderActionsQueries = (
  restaurantUUID: string,
  supplierUUID: string,
  orderDomains: OrderDomain[],
) => {
  const { overrideChatStatus } = useAuthenticatedUserContext();
  const { isUpdatingFulfillment, updateFulfillment } = useUpdateFulfillment({
    restaurantUUID,
    supplierUUID,
  });
  const orderGuideStoreApi = useOrderGuideStoreApi();
  const isOfflineModeEnabled = useOfflineModeEnabled();
  const { syncOfflineOrderContextWithOnlineChange } = useOfflineOrderContext(
    restaurantUUID!,
    supplierUUID!,
  );

  const orderContextQueryVariables = useOrderContextQueryVariables(
    orderDomains,
    restaurantUUID,
    supplierUUID,
  );

  const updateCachedOrderContext = useCallback(
    async (client: DataProxy, updateCacheFn: UpdateCacheFn) => {
      PerfTiming.start(PerfTimingEvent.ORDER_CACHE_UPDATE);
      try {
        const cachedOrder = client.readQuery<
          NewOrder_OrderContextQuery,
          NewOrder_OrderContextQueryVariables
        >({
          query: ORDER_CONTEXT_QUERY,
          variables: orderContextQueryVariables,
        });
        if (cachedOrder) {
          const nextOrders = updateCacheFn(cachedOrder.getOrders);
          const updatedOrderContext = {
            ...cachedOrder,
            getOrders: nextOrders,
          };
          client.writeQuery<
            NewOrder_OrderContextQuery,
            NewOrder_OrderContextQueryVariables
          >({
            data: updatedOrderContext,
            query: ORDER_CONTEXT_QUERY,
            variables: orderContextQueryVariables,
          });

          // In addition to the online operations taking place here, we also need to sync the online
          // draft order changes to the offline state, that way if the user goes offline their draft
          // order will continue to be editable.
          //
          // Only do this if the chat is enabled for Offline Mode, as to not store unnecessary data
          if (isOfflineModeEnabled) {
            await syncOfflineOrderContextWithOnlineChange(updatedOrderContext);
          }
        }
      } catch (e) {
        // This catch block was empty because cache updates fail intermittently, but adding captureException now
        // to make debugging possible cache issues easier, e.g. https://github.com/usepepper/restaurant-app/pull/303
        captureException(e);
      }
      PerfTiming.end(PerfTimingEvent.ORDER_CACHE_UPDATE, {
        restaurantUUID,
        supplierUUID,
      });
    },
    [
      isOfflineModeEnabled,
      orderContextQueryVariables,
      restaurantUUID,
      supplierUUID,
      syncOfflineOrderContextWithOnlineChange,
    ],
  );

  const [createOrder, { loading: isCreatingOrder }] = useMutation<
    NewOrder_CreateOrderMutation,
    NewOrder_CreateOrderMutationVariables
  >(CREATE_ORDER_MUTATION, {
    update: async (client, { data }) => {
      const createdOrder = data?.createOrder.order;
      if (createdOrder) {
        await updateCachedOrderContext(client, upsertCachedOrder(createdOrder));
      }
    },
  });

  const [submitOrder] = useMutation<
    NewOrder_SubmitOrderMutation,
    NewOrder_SubmitOrderMutationVariables
  >(SUBMIT_ORDER_MUTATION, {
    context: {
      ignoreGraphQLErrorCodes: [
        OrderValidationErrorCode.ORDER_ALREADY_PLACED,
        OrderValidationErrorCode.ORDER_VALIDATOR_ERROR,
      ],
    },
    refetchQueries: [
      getOperationName(ORDER_HISTORY_QUERY) as string,
      getOperationName(SEARCH_ORDER_HISTORY_QUERY) as string,
      getOperationName(PAYMENTS_CONTEXT_UNPAID_LIST_QUERY) as string,
      getOperationName(GET_VARIANT_PACK_LAST_PURCHASE_INFO_QUERY) as string,
    ],
    update: async (client, { data }, options) => {
      const returnedOrder = data?.submitOrder.order;
      if (returnedOrder) {
        await updateCachedOrderContext(
          client,
          upsertCachedOrder(returnedOrder),
        );
      } else if (options.variables?.orderUUID) {
        const { orderUUID } = options.variables;
        await updateCachedOrderContext(client, removeCachedOrder(orderUUID));
      }
      if (
        returnedOrder &&
        !UNPLACED_ORDER_STATUSES.includes(returnedOrder.status as OrderStatus)
      ) {
        overrideChatStatus(restaurantUUID);
      }
      // Clear non-persistent memo/notes/request and reset minimized state
      orderGuideStoreApi.getState().resetOrderGuideRestaurantState();
    },
  });

  const [updateCart, { loading: isUpdatingCart }] = useMutation<
    NewOrder_UpdateCartMutation,
    NewOrder_UpdateCartMutationVariables
  >(UPDATE_CART_MUTATION, {
    context: {
      ignoreGraphQLErrorCodes: [OrderValidationErrorCode.ORDER_ALREADY_PLACED],
    },
    update: async (client, { data }, options) => {
      const returnedOrder = data?.updateCart.order;
      if (returnedOrder) {
        await updateCachedOrderContext(
          client,
          upsertCachedOrder(returnedOrder),
        );
      } else if (options.variables?.orderUUID) {
        const { orderUUID } = options.variables;
        await updateCachedOrderContext(client, removeCachedOrder(orderUUID));
      }
      if (
        returnedOrder &&
        !UNPLACED_ORDER_STATUSES.includes(returnedOrder.status as OrderStatus)
      ) {
        overrideChatStatus(restaurantUUID);
      }
    },
  });

  const [deleteOrder] = useMutation<
    NewOrder_DeleteOrderMutation,
    NewOrder_DeleteOrderMutationVariables
  >(DELETE_ORDER_MUTATION, {
    update: async (client, { data }, options) => {
      if (data?.deleteOrder.success && options.variables?.orderUUID) {
        const { orderUUID } = options.variables;
        await updateCachedOrderContext(client, removeCachedOrder(orderUUID));
      }
    },
  });

  const [updateOrderMetadata] = useMutation<
    NewOrder_UpdateOrderMetadataMutation,
    NewOrder_UpdateOrderMetadataMutationVariables
  >(UPDATE_ORDER_METADATA_MUTATION);

  const [updateOrderNotes] = useMutation<
    NewOrder_UpdateOrderNotesMutation,
    NewOrder_UpdateOrderNotesMutationVariables
  >(UPDATE_ORDER_NOTES_MUTATION);

  const [updateOrderPoNumber] = useMutation<
    NewOrder_UpdateOrderPoNumberMutation,
    NewOrder_UpdateOrderPoNumberMutationVariables
  >(UPDATE_ORDER_PO_NUMBER_MUTATION);

  const [updateOrderAdditionalInputs] = useMutation<
    NewOrder_UpdateOrderAdditionalInputsMutation,
    NewOrder_UpdateOrderAdditionalInputsMutationVariables
  >(UPDATE_ORDER_ADDITIONAL_INPUTS_MUTATION);

  const [clearCart] = useMutation<
    NewOrder_ClearCartMutation,
    NewOrder_ClearCartMutationVariables
  >(CLEAR_CART_MUTATION, {
    update: async (client, _result, options) => {
      if (options.variables?.orderUUID) {
        const { orderUUID } = options.variables;
        await updateCachedOrderContext(
          client,
          clearCachedOrderItems(orderUUID),
        );
      }
    },
  });

  return {
    clearCart,
    createOrder,
    deleteOrder,
    isCreatingOrder,
    isUpdatingCart,
    isUpdatingFulfillment,
    submitOrder,
    updateCachedOrderContext,
    updateCart,
    updateFulfillment,
    updateOrderAdditionalInputs,
    updateOrderMetadata,
    updateOrderNotes,
    updateOrderPoNumber,
  };
};
